import { Buffer } from "buffer";
import {
  ComputeBudgetProgram,
  type Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import { withTimeout } from "./async";
import {
  CONFIG,
  EVENT_AUTHORITY,
  FEE_ROUTER_PROGRAM_ID,
  FRONTEND_FEE_RECIPIENT,
  PLATFORM_FEE_RECIPIENT,
  POOL_AUTHORITY,
  PRIORITY_FEE_MICROLAMPORTS,
  PROGRAM_ID,
  PROTOCOL_CONFIG,
  PROTOCOL_FEE_RECIPIENT,
} from "./constants";
import type { Market, TradeSide } from "./types";
import {
  TOKEN_2022_PROGRAM_ID,
  createToken2022AtaIdempotentInstruction,
  getToken2022Ata,
} from "./token2022";

const SWAP_DISCRIMINATOR = Buffer.from("f8c69e91e17587c8", "hex");
const ROUTE_SWAP_DISCRIMINATOR = Buffer.from("72960dc08cfcdd1f", "hex");

function writeU64LE(value: bigint): Buffer {
  if (value < 0n || value > 18_446_744_073_709_551_615n) {
    throw new Error("Amount is outside the supported range");
  }
  const buffer = Buffer.alloc(8);
  let remaining = value;
  for (let index = 0; index < 8; index += 1) {
    buffer[index] = Number(remaining & 0xffn);
    remaining >>= 8n;
  }
  return buffer;
}

export function buildSwapInstruction(params: {
  user: PublicKey;
  market: Market;
  userTokenAccount: PublicKey;
  amount: bigint;
  limit: bigint;
  side: TradeSide;
}): TransactionInstruction {
  const { user, market, userTokenAccount, amount, limit, side } = params;
  const data = Buffer.concat([
    ROUTE_SWAP_DISCRIMINATOR,
    writeU64LE(amount),
    writeU64LE(limit),
    Buffer.from([side === "buy" ? 1 : 0, 0]),
  ]);
  return new TransactionInstruction({
    programId: FEE_ROUTER_PROGRAM_ID,
    data,
    keys: [
      { pubkey: user, isSigner: true, isWritable: true },
      { pubkey: CONFIG, isSigner: false, isWritable: false },
      { pubkey: new PublicKey(market.pool), isSigner: false, isWritable: true },
      { pubkey: new PublicKey(market.mint), isSigner: false, isWritable: false },
      { pubkey: new PublicKey(market.solVault), isSigner: false, isWritable: true },
      { pubkey: new PublicKey(market.tokenVault), isSigner: false, isWritable: true },
      { pubkey: POOL_AUTHORITY, isSigner: false, isWritable: false },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: new PublicKey(market.creator), isSigner: false, isWritable: true },
      { pubkey: PROTOCOL_FEE_RECIPIENT, isSigner: false, isWritable: true },
      { pubkey: PROTOCOL_CONFIG, isSigner: false, isWritable: false },
      { pubkey: TOKEN_2022_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: EVENT_AUTHORITY, isSigner: false, isWritable: false },
      { pubkey: PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: PLATFORM_FEE_RECIPIENT, isSigner: false, isWritable: true },
      { pubkey: user, isSigner: true, isWritable: false },
      { pubkey: FRONTEND_FEE_RECIPIENT, isSigner: false, isWritable: true },
    ],
  });
}

export function buildSwapTransaction(params: {
  user: PublicKey;
  market: Market;
  amount: bigint;
  limit: bigint;
  side: TradeSide;
}): { transaction: Transaction; userTokenAccount: PublicKey } {
  const { user, market, amount, limit, side } = params;
  const mint = new PublicKey(market.mint);
  const userTokenAccount = getToken2022Ata(mint, user);
  const transaction = new Transaction().add(
    ComputeBudgetProgram.setComputeUnitLimit({ units: 250_000 }),
    ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: PRIORITY_FEE_MICROLAMPORTS,
    }),
  );
  if (side === "buy") {
    transaction.add(
      createToken2022AtaIdempotentInstruction({
        payer: user,
        ata: userTokenAccount,
        owner: user,
        mint,
      }),
    );
  }
  transaction.add(
    buildSwapInstruction({ user, market, userTokenAccount, amount, limit, side }),
  );
  return { transaction, userTokenAccount };
}

export async function confirmSwapTransaction(params: {
  connection: Connection;
  fallbackConnection?: Connection;
  signature: string;
}): Promise<void> {
  const { connection, fallbackConnection, signature } = params;
  const connections = [connection];
  if (fallbackConnection && fallbackConnection.rpcEndpoint !== connection.rpcEndpoint) {
    connections.push(fallbackConnection);
  }
  const deadline = Date.now() + 120_000;
  while (Date.now() < deadline) {
    const results = await Promise.all(
      connections.map(async (statusConnection) => {
        try {
          return await withTimeout(
            statusConnection.getSignatureStatuses([signature], {
              searchTransactionHistory: true,
            }),
            5_000,
            "Signature status request timed out",
          );
        } catch {
          return undefined;
        }
      }),
    );
    for (const response of results) {
      const status = response?.value[0];
      if (!status) continue;
      if (status.err) {
        throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
      }
      if (
        status.confirmationStatus === "confirmed" ||
        status.confirmationStatus === "finalized"
      ) {
        return;
      }
    }
    await new Promise((resolve) => globalThis.setTimeout(resolve, 1_500));
  }
  throw new Error(
    `The transaction was sent but its status is still unknown. ` +
      `Do not retry until you check this signature: ${signature}`,
  );
}

export const SWAP_INSTRUCTION_DISCRIMINATOR = SWAP_DISCRIMINATOR.toString("hex");
export const ROUTE_SWAP_INSTRUCTION_DISCRIMINATOR =
  ROUTE_SWAP_DISCRIMINATOR.toString("hex");
